import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, KeyRound, UserX, RefreshCw } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RoleBadge from "@/components/RoleBadge";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function UsersList() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter && roleFilter !== "all") params.role = roleFilter;
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await api.delete(`/users/${deactivateTarget.id}`);
      toast.success(`${deactivateTarget.username} deactivated`);
      setDeactivateTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="users-page">
      <PageHeader
        testId="users-header"
        title="Users"
        subtitle="Manage engineers, office staff, managers and admins."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} data-testid="users-refresh">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            <Button asChild size="sm" data-testid="users-new-button">
              <Link to="/users/new">
                <Plus className="h-4 w-4" /> New User
              </Link>
            </Button>
          </>
        }
      />

      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Filter by role
        </span>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="office">Office</SelectItem>
            <SelectItem value="engineer">Engineer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Username</th>
                <th className="text-left py-2 px-3">Full name</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Role</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">
                    Loading users…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-xs text-muted-foreground" data-testid="users-empty">
                    No users match this filter.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.username}`}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="py-2 px-3 font-medium">{u.username}</td>
                    <td className="py-2 px-3">{u.full_name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{u.email || "—"}</td>
                    <td className="py-2 px-3"><RoleBadge role={u.role} /></td>
                    <td className="py-2 px-3">
                      {u.active ? (
                        <StatusBadge tone="success" testId={`status-${u.username}`}>Active</StatusBadge>
                      ) : (
                        <StatusBadge tone="muted" testId={`status-${u.username}`}>Inactive</StatusBadge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            data-testid={`user-actions-${u.username}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/users/${u.id}/edit`)}
                            data-testid={`user-edit-${u.username}`}
                          >
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setResetTarget(u); setNewPassword(""); }}
                            data-testid={`user-reset-${u.username}`}
                          >
                            <KeyRound className="h-4 w-4 mr-2" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={me?.id === u.id || !u.active}
                            onClick={() => setDeactivateTarget(u)}
                            data-testid={`user-deactivate-${u.username}`}
                            className="text-destructive focus:text-destructive"
                          >
                            <UserX className="h-4 w-4 mr-2" /> Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Reset-password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="max-w-lg rounded-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <b>{resetTarget?.username}</b>. They will need this to log in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-pw">New password</Label>
            <Input
              id="reset-pw"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="reset-password-input"
              placeholder="At least 6 characters"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="reset-password-cancel">
              Cancel
            </Button>
            <Button onClick={handleResetPassword} data-testid="reset-password-submit">
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={!!deactivateTarget} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <DialogContent className="max-w-lg rounded-md" data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate user</DialogTitle>
            <DialogDescription>
              <b>{deactivateTarget?.username}</b> will no longer be able to log in.
              You can reactivate them by editing their profile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} data-testid="deactivate-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeactivate} data-testid="deactivate-confirm">
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
